import { useState, type FormEvent } from 'react';
import { Camera, PencilLine } from 'lucide-react';
import { api } from '../../api';
import { Modal } from '../../componentes/Modal';
import { NOME_SITUACAO, agrupar, resumoRegra, type ChecklistOs as Checklist, type ItemOs } from './comum';

const hora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

function valorTexto(i: ItemOs) {
  if (!i.situacao) return null;
  if (i.situacao === 'na') return `não se aplica — ${i.resposta ?? ''}`;
  if (i.tipo_resposta === 'numero') return `${i.valor_numero}${i.regra.unidade ? ` ${i.regra.unidade}` : ''}`;
  if (i.tipo_resposta === 'ok_nok') return i.resposta?.toUpperCase();
  if (i.tipo_resposta === 'sim_nao') return i.resposta === 'sim' ? 'Sim' : 'Não';
  if (i.tipo_resposta === 'foto') return `${i.foto_ids.length} foto(s)`;
  return i.resposta;
}

/**
 * Checklist na ficha da OS. Antes do check-in mostra o que o técnico vai receber (acompanha a biblioteca);
 * depois, o que foi respondido. Escritório pode responder enquanto a OS está aberta (atendimento remoto).
 */
export function ChecklistOs({ osId, checklist, fotosNoServidor, podeResponder, iniciada, aoMudar }: {
  osId: string; checklist: Checklist; fotosNoServidor: string[]; podeResponder: boolean; iniciada: boolean; aoMudar: () => void;
}) {
  const [editando, setEditando] = useState<ItemOs | null>(null);
  const grupos = agrupar(checklist.itens);
  const nok = checklist.itens.filter((i) => i.situacao === 'nok').length;
  if (!checklist.itens.length && !checklist.sob_demanda.length) return null;

  return (
    <section className="secao">
      <div className="secao-topo">
        <h2>Checklist <span className="contador">{checklist.itens.filter((i) => i.situacao).length}/{checklist.itens.length}</span></h2>
        <div className="meta">
          {checklist.obrigatorios_pendentes > 0 && <span className="pill-breve"><i aria-hidden /> {checklist.obrigatorios_pendentes} obrigatório(s) sem resposta</span>}
          {nok > 0 && <span className="situacao s-nok">{nok} NOK</span>}
        </div>
      </div>
      {!iniciada && <p className="nota">Prévia: o checklist acompanha a biblioteca e os ajustes do cliente até o técnico chegar (check-in). Daí em diante fica congelado.</p>}
      {checklist.sob_demanda.length > 0 && (
        <p className="nota">{checklist.sob_demanda.map((b) => `“${b.nome}”`).join(', ')}: o técnico adiciona no app os equipamentos que atender.</p>
      )}
      {grupos.map((g) => (
        <div key={g.chave} className="cartao grupo-checklist">
          <h3>{g.ativo ?? g.bloco}{g.ativo && <small> · {g.bloco}</small>}</h3>
          <ul>
            {g.itens.map((i) => {
              const v = valorTexto(i);
              const faltam = i.foto_ids.filter((f) => !fotosNoServidor.includes(f)).length;
              return (
                <li key={i.id} className={i.situacao ? '' : 'sem-resposta'}>
                  <div className="item-checklist">
                    <span>{i.regra.bloqueante && <span className="pill-seguranca" title="NOK impede fechar como resolvida e gera aviso ao síndico">segurança</span>}{i.texto}{!i.obrigatorio && <small> · opcional</small>}{i.ajustado && <small> · ajuste do cliente</small>}</span>
                    {i.situacao ? <span className={`situacao s-${i.situacao}`}>{i.situacao === 'na' ? 'N/A' : NOME_SITUACAO[i.situacao]}</span> : <span className="nota">sem resposta</span>}
                  </div>
                  {v && i.situacao !== 'na' && i.tipo_resposta !== 'ok_nok' && <div className="valor-checklist">{v}</div>}
                  {i.situacao === 'na' && <div className="valor-checklist">{i.resposta}</div>}
                  {i.observacao && <div className="valor-checklist">“{i.observacao}”</div>}
                  <small className="nota">
                    {i.foto_ids.length > 0 && <span className="com-icone"><Camera size={12} aria-hidden /> {i.foto_ids.length} foto(s){faltam ? ` · ${faltam} ainda no celular` : ' (veja em Fotos)'} · </span>}
                    {i.respondido_em ? `${i.respondido_por ?? ''} · ${hora(i.respondido_em)}` : resumoRegra({ tipo_resposta: i.tipo_resposta, ...i.regra })}
                  </small>
                  {podeResponder && i.tipo_resposta !== 'foto' && (
                    <button className="btn-link" onClick={() => setEditando(i)}><PencilLine size={13} aria-hidden /> {i.situacao ? 'corrigir' : 'responder'}</button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <Modal titulo={editando?.texto ?? ''} aberto={Boolean(editando)} aoFechar={() => setEditando(null)}>
        {editando && <FormResposta osId={osId} item={editando} aoSalvar={() => { setEditando(null); aoMudar(); }} aoCancelar={() => setEditando(null)} />}
      </Modal>
    </section>
  );
}

function FormResposta({ osId, item, aoSalvar, aoCancelar }: { osId: string; item: ItemOs; aoSalvar: () => void; aoCancelar: () => void }) {
  const [resposta, setResposta] = useState(item.situacao === 'na' ? '' : item.resposta ?? (item.tipo_resposta === 'numero' ? '' : ''));
  const [numero, setNumero] = useState(item.valor_numero ?? '');
  const [obs, setObs] = useState(item.observacao ?? '');
  const [na, setNa] = useState(item.situacao === 'na');
  const [motivoNa, setMotivoNa] = useState(item.situacao === 'na' ? item.resposta ?? '' : '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const opcoes = item.tipo_resposta === 'ok_nok' ? ['ok', 'nok'] : item.tipo_resposta === 'sim_nao' ? ['sim', 'nao'] : item.tipo_resposta === 'opcao' ? item.regra.opcoes ?? [] : [];

  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    const r = {
      ativo_id: item.ativo_id, bloco_id: item.bloco_id, codigo: item.codigo, respondido_em: new Date().toISOString(), foto_ids: item.foto_ids,
      ...(na ? { na: true, resposta: motivoNa } : item.tipo_resposta === 'numero' ? { valor_numero: Number(String(numero).replace(',', '.')) } : { resposta }),
      observacao: obs.trim() || null,
    };
    try {
      const out = await api<{ recusadas: { motivo: string }[] }>(`/os/${osId}/checklist`, { metodo: 'POST', corpo: { respostas: [r] } });
      if (out.recusadas.length) setErro(out.recusadas[0]!.motivo); else aoSalvar();
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      {item.ajuda && <p className="nota">{item.ajuda}</p>}
      <label className="check"><input type="checkbox" checked={na} onChange={(e) => setNa(e.target.checked)} /> Não se aplica</label>
      {na ? <label>Por quê<input required minLength={3} value={motivoNa} onChange={(e) => setMotivoNa(e.target.value)} /></label>
        : opcoes.length ? (
          <div className="segmentos">{opcoes.map((o) => <button type="button" key={o} className={resposta === o ? 'ativo' : ''} onClick={() => setResposta(o)}>{o === 'nao' ? 'Não' : o === 'sim' ? 'Sim' : o.toUpperCase() === o || o.length <= 3 ? o.toUpperCase() : o}</button>)}</div>
        ) : item.tipo_resposta === 'numero' ? (
          <label>Valor{item.regra.unidade ? ` (${item.regra.unidade})` : ''}<input required inputMode="decimal" value={numero} onChange={(e) => setNumero(e.target.value)} /></label>
        ) : <label>Resposta<textarea required rows={3} maxLength={2000} value={resposta} onChange={(e) => setResposta(e.target.value)} /></label>}
      <label>Observação (opcional)<input maxLength={2000} value={obs} onChange={(e) => setObs(e.target.value)} /></label>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Voltar</button>
        <button className="btn" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar resposta'}</button>
      </div>
    </form>
  );
}
